import { useState } from 'react';
import { Link } from 'react-router';
import { useOrderedBoards } from '../../hooks/useBoards';
import { useT } from '../../i18n';
import { ofBoard, openCards, useCards } from '../../store/cards';
import { useSession } from '../../store/session';
import controls from '../controls.module.css';
import { Check, ChevronDown } from '../icons';
import { Sheet } from '../Sheet/Sheet';
import styles from './BoardSwitch.module.css';

/** The list of boards, to jump to one; creating, renaming and ordering live in Settings. */
export function BoardPicker({ open, onClose }: { open: boolean; onClose(): void }) {
  const t = useT();
  const boards = useOrderedBoards();
  const boardId = useSession((s) => s.boardId);
  const setBoard = useSession((s) => s.setBoard);
  const cards = useCards((s) => s.cards);
  const counts = (id: string) => openCards(ofBoard(cards, id)).length;
  return (
    <Sheet open={open} label={t.t('boards.title')} onClose={onClose}>
      <div className={controls.group}>
        <div className={controls.label}>{t.t('boards.title')}</div>
        <div className={styles.list} role="radiogroup" aria-label={t.t('boards.title')}>
          {boards.map((b) => (
            <button
              key={b.id}
              type="button"
              role="radio"
              aria-checked={b.id === boardId}
              className={styles.row}
              onClick={() => {
                setBoard(b.id);
                onClose();
              }}
            >
              <span className={styles.rowName}>{b.name}</span>
              <span className={styles.rowCount}>{t.tn('count.cards', counts(b.id))}</span>
              {b.id === boardId ? <Check size={18} /> : null}
            </button>
          ))}
        </div>
      </div>
      <div className={controls.footer}>
        <Link to="/settings#boards" className={`${controls.btn} ${controls.quiet} ${controls.footerLeft} ${styles.manage}`} onClick={onClose}>
          {t.t('boards.manage')}
        </Link>
        <button type="button" className={`${controls.btn} ${controls.primary}`} onClick={onClose}>
          {t.t('sheet.close')}
        </button>
      </div>
    </Sheet>
  );
}

/** Desktop: the board's name in the top bar opens the list. */
export function BoardSwitch() {
  const t = useT();
  const boards = useOrderedBoards();
  const boardId = useSession((s) => s.boardId);
  const [open, setOpen] = useState(false);
  const current = boards.find((b) => b.id === boardId);
  const single = boards.length <= 1;
  return (
    <>
      <button type="button" className={`display ${styles.name}`} onClick={() => setOpen(true)} aria-label={t.t('boards.switch')} title={t.t('boards.shortcut')} data-board-switch="">
        {single ? t.t('app.name') : (current?.name ?? t.t('app.name'))}
        <ChevronDown size={14} />
      </button>
      <BoardPicker open={open} onClose={() => setOpen(false)} />
    </>
  );
}
